import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Platform,
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as Location from 'expo-location';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { StorefrontHeader } from '../components/storefront/StorefrontHeader';
import { MapLocationPicker } from '../components/MapLocationPicker';
import { useCartStore } from '../store/cartStore';
import { useStorefront } from '../hooks/useStorefront';
import { useThemedRTLStyles } from '../hooks/useThemedRTLStyles';
import { useLocale } from '../contexts/LocaleContext';
import { colors, typography } from '../theme';
import { apiService } from '../services/api';
import { getCurrencySymbol } from '../utils/currencies';
import { getImageUrlSync, getApiBaseURL } from '../utils/imageProxy';
import { unitPriceOf } from './CartScreen';
import { useOrderMinimums } from '../hooks/useOrderMinimums';
import { OrderMinimumsNotice } from '../components/OrderMinimumsNotice';
import { businessCurrency } from '../utils/businessDefaults';

interface PriceLine {
  label: string;
  amount: number;
  type: 'subtotal' | 'tax' | 'charge' | 'discount' | 'total';
}

type FieldErrors = Partial<Record<'name' | 'phone' | 'email' | 'shipping', string>>;

const safeStr = (v: any): string => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return v.message || v.msg || JSON.stringify(v);
  return String(v);
};

const formatMoney = (currency: string, amount: number) => `${currency} ${Number(amount || 0).toFixed(2)}`;
const isValidPhone = (v: string) => v.replace(/\D/g, '').length >= 7;
const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const apiBase = async () => {
  const { getCurrentApiConfig } = await import('../config/api.config');
  return getCurrentApiConfig().baseURL;
};

// Module-level so React keeps the same component type across renders — a
// component declared inside render remounts on every keystroke and the
// inputs inside it lose focus.
const Card: React.FC<{ s: any; title?: string; icon?: string; right?: React.ReactNode; children: React.ReactNode }> = ({ s, title, icon, right, children }) => (
  <View style={s.card}>
    {title ? (
      <View style={s.cardHeader}>
        {icon ? <Ionicons name={icon as any} size={18} color={colors.primary as string} /> : null}
        <Text style={s.cardTitle}>{title}</Text>
        {right}
      </View>
    ) : null}
    {children}
  </View>
);

// Keyboard avoidance is only needed on native; on web the wrapper must be a
// plain shrinkable view or the footer gets pushed below the viewport.
const BodyWrapper: any = Platform.OS === 'web' ? View : KeyboardAvoidingView;

export const CheckoutScreen: React.FC = () => {
  const s = useThemedRTLStyles(getStyles);
  const { t, isRTL } = useLocale();
  const insets = useSafeAreaInsets();
  const { params, go } = useStorefront();
  const { items, totalPrice, totalItems, clearCart } = useCartStore();
  const scrollRef = useRef<ScrollView>(null);

  const { businessId, catalogId, contactId, conversationId, language } = params;
  const linkPhone = params.phoneNumber || '';
  const quickReplyItemId = params.quickReplyItemId;
  const currency = getCurrencySymbol(items[0]?.product.currency || businessCurrency());

  const STEPS = [t('storefront.checkout.stepDetails'), t('storefront.checkout.stepDelivery'), t('storefront.checkout.stepReview')];
  const [step, setStep] = useState(0);

  // ── Form ──────────────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [phone, setPhone] = useState(linkPhone);
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [location, setLocation] = useState<{ latitude: number; longitude: number; address: string } | null>(null);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [apiBaseURL, setApiBaseURL] = useState<string | null>(null);

  // ── Pricing ───────────────────────────────────────────────────────────────
  const [businessConfig, setBusinessConfig] = useState<any>(null);
  const [priceLines, setPriceLines] = useState<PriceLine[]>([]);
  const [finalTotal, setFinalTotal] = useState(totalPrice);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [optionalCharges, setOptionalCharges] = useState<Record<string, boolean>>({});
  const [optionalChargeDefs, setOptionalChargeDefs] = useState<{ name: string; amount: number }[]>([]);

  const [couponInput, setCouponInput] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [referralInput, setReferralInput] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralDiscount, setReferralDiscount] = useState(0);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralError, setReferralError] = useState('');

  const [shippingMethods, setShippingMethods] = useState<any[]>([]);
  const [selectedShippingId, setSelectedShippingId] = useState<string | null>(null);
  const [expressSelected, setExpressSelected] = useState(false);
  const [shippingLoading, setShippingLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  // Same product/vendor minimums the server enforces on submit.
  const minimumsLines = useMemo(
    () => items.map((i) => ({ productId: i.productId, variantId: i.variantId, quantity: i.quantity, lineTotal: unitPriceOf(i) * i.quantity })),
    [items]
  );
  const { report: minimums, ok: minimumsOk } = useOrderMinimums(businessId, minimumsLines, items[0]?.product.currency);

  useEffect(() => {
    getApiBaseURL().then(setApiBaseURL).catch(() => {});
  }, []);

  // ── Prefill from the contact the link was sent to ─────────────────────────
  useEffect(() => {
    if (!businessId || !contactId) return;
    apiService
      .getCheckoutPrefill(businessId, contactId)
      .then((r) => {
        const c = r.statusCode === 1000 && r.data?.found ? r.data.contact : undefined;
        if (!c) return;
        setName((v) => v || c.name || '');
        setPhone((v) => v || c.phone || '');
        setEmail((v) => v || c.email || '');
        setAddress((v) => v || c.address || '');
      })
      .catch(() => {});
  }, [businessId, contactId]);

  // ── Business config (referral) ────────────────────────────────────────────
  useEffect(() => {
    if (!businessId) return;
    (async () => {
      try {
        const data = await (await fetch(`${await apiBase()}/api/business-config?businessId=${businessId}`)).json();
        if (data.statusCode === 1000 && data.data) setBusinessConfig(data.data);
      } catch {}
    })();
  }, [businessId]);

  // ── Location: best-effort auto-detect ─────────────────────────────────────
  const detectLocation = useCallback(async () => {
    setDetectingLocation(true);
    try {
      if (Platform.OS === 'web') {
        await new Promise<void>((resolve) => {
          if (!navigator?.geolocation?.getCurrentPosition) return resolve();
          navigator.geolocation.getCurrentPosition(
            async ({ coords: { latitude, longitude } }) => {
              try {
                const r = await fetch(
                  `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1&accept-language=${isRTL ? 'ar' : 'en'}`
                );
                const d = await r.json();
                setLocation({ latitude, longitude, address: d.display_name || `${latitude.toFixed(5)}, ${longitude.toFixed(5)}` });
              } catch {
                setLocation({ latitude, longitude, address: `${latitude.toFixed(5)}, ${longitude.toFixed(5)}` });
              }
              resolve();
            },
            () => resolve(),
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
          );
        });
      } else {
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (status === 'granted') {
          const pos = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced });
          const geo = await Location.reverseGeocodeAsync({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
          const addr = geo[0] ? [geo[0].street, geo[0].district, geo[0].city, geo[0].region].filter(Boolean).join(', ') : '';
          setLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            address: addr || `${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`,
          });
        }
      }
    } catch {
      // Location is optional — the picker is always available.
    } finally {
      setDetectingLocation(false);
    }
  }, [isRTL]);

  useEffect(() => {
    detectLocation();
  }, [detectLocation]);

  // ── Shipping methods (per location) ───────────────────────────────────────
  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    setShippingLoading(true);
    (async () => {
      try {
        const res = await fetch(`${await apiBase()}/api/shipping/for-location`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ businessId, latitude: location?.latitude, longitude: location?.longitude, orderAmount: totalPrice }),
        });
        const data = await res.json();
        if (cancelled) return;
        const methods = data.statusCode === 1000 && Array.isArray(data.data) ? data.data : [];
        setShippingMethods(methods);
        setSelectedShippingId((current) => {
          if (current && methods.some((m: any) => m._id === current)) return current;
          if (methods.length === 0) return null;
          // Preselect the cheapest option.
          return [...methods].sort((a, b) => Number(a.price || 0) - Number(b.price || 0))[0]._id;
        });
      } catch {
        if (!cancelled) setShippingMethods([]);
      } finally {
        if (!cancelled) setShippingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId, location, totalPrice]);

  // ── Pricing (taxes / charges / shipping / discounts) ──────────────────────
  const computePricing = useCallback(async () => {
    const subtotal = totalPrice;
    const lines: PriceLine[] = [{ label: t('storefront.checkout.subtotal'), amount: subtotal, type: 'subtotal' }];
    let taxTotal = 0;
    let chargesTotal = 0;
    const newOptionalDefs: { name: string; amount: number }[] = [];

    if (businessId) {
      setPricingLoading(true);
      try {
        const res = await fetch(`${await apiBase()}/api/business-config/compute-charges`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            businessId,
            subtotal,
            orderProductIds: items.map((i) => i.productId).filter(Boolean),
            catalogueIds: catalogId ? [catalogId] : [],
            location: location ? { latitude: location.latitude, longitude: location.longitude } : undefined,
          }),
        });
        const data = await res.json();
        if (data.statusCode === 1000 && data.data) {
          (data.data.taxes || []).forEach((tx: any) => {
            taxTotal += tx.amount;
            lines.push({ label: `${tx.name} (${tx.rate}%)`, amount: tx.amount, type: 'tax' });
          });
          (data.data.charges || []).forEach((c: any) => {
            if (c.mandatory) {
              chargesTotal += c.amount;
              lines.push({ label: c.name, amount: c.amount, type: 'charge' });
            } else {
              newOptionalDefs.push({ name: c.name, amount: c.amount });
              if (optionalCharges[c.name]) {
                chargesTotal += c.amount;
                lines.push({ label: t('storefront.checkout.optionalLine', { name: c.name }), amount: c.amount, type: 'charge' });
              }
            }
          });
        }
      } catch {
        // No taxes/charges on failure.
      } finally {
        setPricingLoading(false);
      }
    }
    setOptionalChargeDefs(newOptionalDefs);

    const shipping = shippingMethods.find((m) => m._id === selectedShippingId);
    if (shipping) {
      if (Number(shipping.price) > 0) {
        chargesTotal += Number(shipping.price);
        lines.push({ label: t('storefront.checkout.shippingLine', { name: shipping.name }), amount: Number(shipping.price), type: 'charge' });
      }
      if (expressSelected && shipping.expressOption?.enabled && shipping.expressOption.extraCost > 0) {
        chargesTotal += shipping.expressOption.extraCost;
        lines.push({ label: shipping.expressOption.label || t('storefront.checkout.express'), amount: shipping.expressOption.extraCost, type: 'charge' });
      }
    }

    if (couponDiscount > 0) lines.push({ label: t('storefront.checkout.coupon', { code: couponCode }), amount: -couponDiscount, type: 'discount' });
    if (referralDiscount > 0) lines.push({ label: t('storefront.checkout.referralLine', { code: referralCode }), amount: -referralDiscount, type: 'discount' });
    const total = Math.max(0, subtotal + taxTotal + chargesTotal - couponDiscount - referralDiscount);
    lines.push({ label: t('storefront.checkout.total'), amount: total, type: 'total' });
    setPriceLines(lines);
    setFinalTotal(total);
  }, [totalPrice, businessId, catalogId, items, location, optionalCharges, couponDiscount, couponCode, referralDiscount, referralCode, shippingMethods, selectedShippingId, expressSelected, t]);

  useEffect(() => {
    computePricing();
  }, [computePricing]);

  // ── Codes ─────────────────────────────────────────────────────────────────
  const validateCode = async (kind: 'coupon' | 'referral') => {
    const raw = kind === 'coupon' ? couponInput : referralInput;
    const code = raw.trim().toUpperCase();
    if (!code) return;
    const setLoading = kind === 'coupon' ? setCouponLoading : setReferralLoading;
    const setError = kind === 'coupon' ? setCouponError : setReferralError;
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${await apiBase()}/api/${kind === 'coupon' ? 'coupons' : 'referrals'}/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessId, code, orderAmount: totalPrice }),
      });
      const data = await res.json();
      if (data.statusCode === 1000 && data.data?.valid === true) {
        if (kind === 'coupon') {
          setCouponCode(code);
          setCouponDiscount(data.data.discountAmount || 0);
        } else {
          setReferralCode(code);
          setReferralDiscount(data.data.referredDiscount || data.data.discountAmount || 0);
        }
      } else {
        setError(data.data?.reason || safeStr(data.errors?.[0]) || t('storefront.checkout.invalidCode'));
      }
    } catch {
      setError(t('storefront.checkout.codeCheckFailed'));
    } finally {
      setLoading(false);
    }
  };

  // ── Steps ─────────────────────────────────────────────────────────────────
  const validateDetails = (): boolean => {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = t('storefront.checkout.nameRequired');
    if (!isValidPhone(phone)) next.phone = t('storefront.checkout.phoneRequired');
    if (email.trim() && !isValidEmail(email.trim())) next.email = t('storefront.checkout.emailInvalid');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateDelivery = (): boolean => {
    if (shippingMethods.length > 0 && !selectedShippingId) {
      setErrors({ shipping: t('storefront.checkout.shippingRequired') });
      return false;
    }
    setErrors({});
    return true;
  };

  const goStep = (next: number) => {
    setSubmitError(null);
    setStep(next);
    scrollRef.current?.scrollTo({ y: 0, animated: false });
  };

  const handleBack = () => {
    if (step > 0) return goStep(step - 1);
    go('cart');
  };

  const handleNext = () => {
    if (!minimumsOk) return setSubmitError(t('orderMinimums.title'));
    if (step === 0 && !validateDetails()) return;
    if (step === 1 && !validateDelivery()) return;
    goStep(step + 1);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitError(null);
    if (!businessId) return setSubmitError(t('storefront.checkout.missingStore'));
    if (items.length === 0) return setSubmitError(t('storefront.checkout.cartEmpty'));
    if (!minimumsOk) return setSubmitError(t('orderMinimums.title'));
    if (!validateDetails()) return goStep(0);
    setSubmitting(true);
    try {
      const response = await apiService.submitOrder({
        businessId,
        // WhatsApp links carry the customer's WhatsApp number — the invoice
        // goes there. Otherwise (Messenger etc.) the typed phone is used.
        phoneNumber: linkPhone || phone.trim(),
        items: items.map((item) => ({
          productId: item.productId,
          variantId: item.variantId,
          quantity: item.quantity,
          product: {
            _id: item.product._id,
            name: item.product.name,
            price: item.product.price,
            salePrice: item.product.salePrice,
            currency: item.product.currency,
            primaryImage: item.product.primaryImage,
            brand: item.product.brand,
            sku: item.product.sku,
          },
          variant: item.variant ? { id: item.variant.id, name: item.variant.name, price: item.variant.price } : undefined,
          selectedAddons: item.selectedAddons,
        })),
        customer: {
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim() || undefined,
          address: address.trim() || undefined,
          location: location || undefined,
        },
        subtotal: totalPrice,
        tax: priceLines.filter((l) => l.type === 'tax').reduce((sum, l) => sum + l.amount, 0),
        extraCharges: priceLines.filter((l) => l.type === 'charge').map((l) => ({ name: l.label, amount: l.amount })),
        discount: couponDiscount + referralDiscount,
        couponCode: couponCode || undefined,
        referralCode: referralCode || undefined,
        totalPrice: finalTotal,
        notes: notes.trim() || undefined,
        catalogId: catalogId || undefined,
        quickReplyItemId: quickReplyItemId || undefined,
        contactId: contactId || undefined,
        conversationId: conversationId || undefined,
        language: language || (isRTL ? 'ar' : 'en'),
      });
      if (response.statusCode === 1000 || response.statusCode === 200) {
        const order = (response.data as any)?.order;
        clearCart();
        go('order-success', {
          orderNumber: order?.orderNumber ? String(order.orderNumber) : undefined,
          total: formatMoney(currency, finalTotal),
          notifiedVia: (response.data as any)?.notifiedVia,
          phoneNumber: linkPhone || phone.trim(),
        });
      } else {
        throw new Error(safeStr(response.errors?.[0]) || t('storefront.checkout.orderFailed'));
      }
    } catch (e: any) {
      const msg = e?.errors?.[0] ? safeStr(e.errors[0]) : e?.message;
      setSubmitError(msg && !/network|HTTP error/i.test(msg) ? msg : t('storefront.checkout.orderFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const totalLine = priceLines.find((l) => l.type === 'total');
  const extraLines = priceLines.filter((l) => l.type !== 'subtotal' && l.type !== 'total');
  const total = totalLine?.amount ?? finalTotal;
  const selectedShipping = useMemo(() => shippingMethods.find((m) => m._id === selectedShippingId), [shippingMethods, selectedShippingId]);

  if (items.length === 0 && !submitting) {
    return (
      <View style={s.screen}>
        <StorefrontHeader title={t('storefront.checkout.title')} onBack={() => go('cart')} />
        <View style={s.center}>
          <Ionicons name="bag-outline" size={40} color={colors.text.secondary as string} />
          <Text style={s.emptyText}>{t('storefront.checkout.cartEmpty')}</Text>
          {(params.catalogId || params.productId) ? (
            <TouchableOpacity style={s.primaryBtnSmall} onPress={() => go(params.catalogId ? 'catalog-view' : 'product-view')}>
              <Text style={s.primaryBtnText}>{t('storefront.cart.startShopping')}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    );
  }

  // ── UI pieces ─────────────────────────────────────────────────────────────
  const field = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    opts: { placeholder?: string; error?: string; keyboardType?: any; multiline?: boolean; autoComplete?: any; textContentType?: any } = {}
  ) => (
    <View style={s.field}>
      <Text style={s.label}>{label}</Text>
      <TextInput
        style={[s.input, opts.multiline && s.textArea, !!opts.error && s.inputError]}
        value={value}
        onChangeText={onChange}
        placeholder={opts.placeholder}
        placeholderTextColor={colors.text.disabled as string}
        keyboardType={opts.keyboardType}
        multiline={opts.multiline}
        numberOfLines={opts.multiline ? 3 : 1}
        autoComplete={opts.autoComplete}
        textContentType={opts.textContentType}
        autoCapitalize={opts.keyboardType === 'email-address' ? 'none' : undefined}
      />
      {opts.error ? <Text style={s.errorText}>{opts.error}</Text> : null}
    </View>
  );

  const codeBox = (kind: 'coupon' | 'referral') => {
    const applied = kind === 'coupon' ? couponCode : referralCode;
    const amount = kind === 'coupon' ? couponDiscount : referralDiscount;
    const input = kind === 'coupon' ? couponInput : referralInput;
    const setInput = kind === 'coupon' ? setCouponInput : setReferralInput;
    const loading = kind === 'coupon' ? couponLoading : referralLoading;
    const error = kind === 'coupon' ? couponError : referralError;
    if (applied) {
      return (
        <View style={s.appliedCode}>
          <Ionicons name="checkmark-circle" size={20} color={colors.success as string} />
          <View style={{ flex: 1 }}>
            <Text style={s.appliedCodeText}>{applied}</Text>
            <Text style={s.appliedCodeSave}>{t('storefront.checkout.youSave', { amount: formatMoney(currency, amount) })}</Text>
          </View>
          <TouchableOpacity
            hitSlop={10}
            onPress={() => {
              if (kind === 'coupon') {
                setCouponCode('');
                setCouponDiscount(0);
                setCouponInput('');
              } else {
                setReferralCode('');
                setReferralDiscount(0);
                setReferralInput('');
              }
            }}
          >
            <Ionicons name="close-circle" size={22} color={colors.text.secondary as string} />
          </TouchableOpacity>
        </View>
      );
    }
    return (
      <>
        <View style={s.codeRow}>
          <TextInput
            style={[s.input, { flex: 1 }]}
            value={input}
            onChangeText={(v) => {
              setInput(v);
              (kind === 'coupon' ? setCouponError : setReferralError)('');
            }}
            placeholder={kind === 'coupon' ? t('storefront.checkout.promoPlaceholder') : t('storefront.checkout.referralPlaceholder')}
            placeholderTextColor={colors.text.disabled as string}
            autoCapitalize="characters"
            returnKeyType="done"
            onSubmitEditing={() => validateCode(kind)}
          />
          <TouchableOpacity
            style={[s.codeBtn, (!input.trim() || loading) && { opacity: 0.5 }]}
            disabled={!input.trim() || loading}
            onPress={() => validateCode(kind)}
          >
            {loading ? (
              <ActivityIndicator size="small" color={colors.text.onPrimary as string} />
            ) : (
              <Text style={s.codeBtnText}>{t('storefront.checkout.apply')}</Text>
            )}
          </TouchableOpacity>
        </View>
        {error ? <Text style={s.errorText}>{error}</Text> : null}
      </>
    );
  };

  const summaryCard = (title: string) => (
    <Card s={s} title={title} icon="receipt-outline" right={pricingLoading ? <ActivityIndicator size="small" color={colors.primary as string} /> : null}>
      <View style={s.priceRow}>
        <Text style={s.priceLabel}>{t('storefront.checkout.subtotal')}</Text>
        <Text style={s.priceVal}>{formatMoney(currency, totalPrice)}</Text>
      </View>
      {extraLines.map((line, i) => (
        <View key={i} style={s.priceRow}>
          <Text style={[s.priceLabel, line.amount < 0 && s.positive]} numberOfLines={2}>{line.label}</Text>
          <Text style={[s.priceVal, line.amount < 0 && s.positive]}>
            {line.amount < 0 ? `−${formatMoney(currency, Math.abs(line.amount))}` : formatMoney(currency, line.amount)}
          </Text>
        </View>
      ))}
      <View style={s.divider} />
      <View style={s.priceRow}>
        <Text style={s.totalLabel}>{t('storefront.checkout.total')}</Text>
        <Text style={s.totalVal}>{formatMoney(currency, total)}</Text>
      </View>
      <View style={s.payNote}>
        <Ionicons name="cash-outline" size={16} color={colors.success as string} />
        <Text style={s.payNoteText}>{t('storefront.checkout.payOnDelivery')}</Text>
      </View>
    </Card>
  );

  // ── Steps ─────────────────────────────────────────────────────────────────
  const renderDetails = () => (
    <>
      <Card s={s} title={t('storefront.checkout.contact')} icon="person-outline">
        {field(t('storefront.checkout.name'), name, (v) => { setName(v); setErrors((e) => ({ ...e, name: undefined })); }, {
          placeholder: t('storefront.checkout.namePlaceholder'),
          error: errors.name,
          autoComplete: 'name',
          textContentType: 'name',
        })}
        {field(t('storefront.checkout.phone'), phone, (v) => { setPhone(v); setErrors((e) => ({ ...e, phone: undefined })); }, {
          placeholder: t('storefront.checkout.phonePlaceholder'),
          error: errors.phone,
          keyboardType: 'phone-pad',
          autoComplete: 'tel',
          textContentType: 'telephoneNumber',
        })}
        {field(t('storefront.checkout.email'), email, (v) => { setEmail(v); setErrors((e) => ({ ...e, email: undefined })); }, {
          placeholder: 'you@example.com',
          error: errors.email,
          keyboardType: 'email-address',
          autoComplete: 'email',
          textContentType: 'emailAddress',
        })}
      </Card>

      <Card s={s} title={t('storefront.checkout.location')} icon="location-outline">
        <TouchableOpacity style={s.locationBtn} onPress={() => setShowLocationPicker(true)} activeOpacity={0.8}>
          <View style={[s.locationIcon, location && s.locationIconActive]}>
            {detectingLocation ? (
              <ActivityIndicator size="small" color={colors.primary as string} />
            ) : (
              <Ionicons name={location ? 'location' : 'location-outline'} size={18} color={(location ? colors.text.onPrimary : colors.text.secondary) as string} />
            )}
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={location ? s.locationAddr : s.locationPlaceholder} numberOfLines={2}>
              {location ? location.address : detectingLocation ? t('storefront.checkout.detectingLocation') : t('storefront.checkout.setLocation')}
            </Text>
            {!location ? <Text style={s.hint}>{t('storefront.checkout.locationHint')}</Text> : null}
          </View>
          <Text style={s.linkText}>{location ? t('storefront.checkout.changeLocation') : ''}</Text>
          <DirectionalIcon name="chevron-forward" size={18} color={colors.text.secondary as string} />
        </TouchableOpacity>
        <View style={{ height: 12 }} />
        {field(t('storefront.checkout.address'), address, setAddress, { placeholder: t('storefront.checkout.addressPlaceholder'), multiline: true, autoComplete: 'street-address' })}
        {field(t('storefront.checkout.notes'), notes, setNotes, { placeholder: t('storefront.checkout.notesPlaceholder'), multiline: true })}
      </Card>
    </>
  );

  const renderDelivery = () => (
    <>
      {(shippingLoading || shippingMethods.length > 0) && (
        <Card s={s} title={t('storefront.checkout.shipping')} icon="bicycle-outline">
          {shippingLoading && shippingMethods.length === 0 ? (
            <View style={s.inlineRow}>
              <ActivityIndicator size="small" color={colors.primary as string} />
              <Text style={s.hint}>{t('storefront.checkout.findingShipping')}</Text>
            </View>
          ) : (
            <>
              {shippingMethods.map((m) => {
                const selected = selectedShippingId === m._id;
                const estimate = expressSelected && selected && m.expressOption?.estimatedDays ? m.expressOption.estimatedDays : m.estimatedDays;
                return (
                  <TouchableOpacity
                    key={m._id}
                    style={[s.option, selected && s.optionSelected]}
                    onPress={() => {
                      setSelectedShippingId(m._id);
                      setExpressSelected(false);
                      setErrors({});
                    }}
                    activeOpacity={0.8}
                  >
                    <View style={[s.radio, selected && s.radioOn]}>{selected ? <View style={s.radioDot} /> : null}</View>
                    <View style={{ flex: 1 }}>
                      <Text style={s.optionName}>{m.name}</Text>
                      {m.description ? <Text style={s.hint}>{m.description}</Text> : null}
                      {estimate ? <Text style={s.optionDays}>{t('storefront.checkout.days', { min: estimate.min, max: estimate.max })}</Text> : null}
                    </View>
                    <Text style={s.optionPrice}>{Number(m.price) === 0 ? t('storefront.checkout.free') : formatMoney(currency, Number(m.price))}</Text>
                  </TouchableOpacity>
                );
              })}
              {selectedShipping?.expressOption?.enabled ? (
                <TouchableOpacity style={s.option} onPress={() => setExpressSelected((v) => !v)} activeOpacity={0.8}>
                  <View style={[s.checkbox, expressSelected && s.checkboxOn]}>
                    {expressSelected ? <Ionicons name="checkmark" size={14} color={colors.text.onPrimary as string} /> : null}
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={s.optionName}>{selectedShipping.expressOption.label || t('storefront.checkout.express')}</Text>
                    {selectedShipping.expressOption.estimatedDays ? (
                      <Text style={s.optionDays}>
                        {t('storefront.checkout.days', { min: selectedShipping.expressOption.estimatedDays.min, max: selectedShipping.expressOption.estimatedDays.max })}
                      </Text>
                    ) : null}
                  </View>
                  <Text style={s.optionPrice}>
                    {selectedShipping.expressOption.extraCost > 0 ? `+${formatMoney(currency, selectedShipping.expressOption.extraCost)}` : t('storefront.checkout.free')}
                  </Text>
                </TouchableOpacity>
              ) : null}
              {errors.shipping ? <Text style={s.errorText}>{errors.shipping}</Text> : null}
            </>
          )}
        </Card>
      )}

      {optionalChargeDefs.length > 0 && (
        <Card s={s} title={t('storefront.checkout.optionalAddons')} icon="add-circle-outline">
          {optionalChargeDefs.map((c) => {
            const on = !!optionalCharges[c.name];
            return (
              <TouchableOpacity key={c.name} style={s.option} onPress={() => setOptionalCharges((p) => ({ ...p, [c.name]: !p[c.name] }))} activeOpacity={0.8}>
                <View style={[s.checkbox, on && s.checkboxOn]}>{on ? <Ionicons name="checkmark" size={14} color={colors.text.onPrimary as string} /> : null}</View>
                <Text style={[s.optionName, { flex: 1 }]}>{c.name}</Text>
                <Text style={s.optionPrice}>+{formatMoney(currency, c.amount)}</Text>
              </TouchableOpacity>
            );
          })}
        </Card>
      )}

      <Card s={s} title={t('storefront.checkout.promo')} icon="pricetag-outline">
        {codeBox('coupon')}
      </Card>
      {businessConfig?.referral?.enabled ? (
        <Card s={s} title={t('storefront.checkout.referral')} icon="gift-outline">
          {codeBox('referral')}
        </Card>
      ) : null}

      {summaryCard(t('storefront.checkout.summary'))}
    </>
  );

  const renderReview = () => (
    <>
      <Card s={s} title={t('storefront.checkout.orderItems')} icon="bag-check-outline">
        {items.map((item, i) => {
          const img = item.product.primaryImage || item.product.images?.[0]?.url;
          const unit = unitPriceOf(item);
          return (
            <View key={i} style={[s.reviewItem, i < items.length - 1 && s.reviewItemBorder]}>
              <View style={s.reviewImg}>
                {img ? (
                  Platform.OS === 'web' ? (
                    <img src={getImageUrlSync(img, apiBaseURL)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' } as any} />
                  ) : (
                    <Image source={{ uri: getImageUrlSync(img, apiBaseURL) }} style={{ width: '100%', height: '100%' }} />
                  )
                ) : (
                  <Ionicons name="image-outline" size={20} color={colors.text.disabled as string} />
                )}
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={s.reviewName} numberOfLines={2}>{item.product.name}</Text>
                {item.variant ? <Text style={s.hint}>{item.variant.name}</Text> : null}
                {item.selectedAddons?.length ? (
                  <Text style={s.hint} numberOfLines={2}>+ {item.selectedAddons.map((a) => a.name).join(isRTL ? '، ' : ', ')}</Text>
                ) : null}
                <Text style={s.hint}>×{item.quantity} · {formatMoney(currency, unit)}</Text>
              </View>
              <Text style={s.reviewPrice}>{formatMoney(currency, unit * item.quantity)}</Text>
            </View>
          );
        })}
      </Card>

      <Card
        s={s}
        title={t('storefront.checkout.deliverTo')}
        icon="location-outline"
        right={
          <TouchableOpacity onPress={() => goStep(0)} hitSlop={8}>
            <Text style={s.linkText}>{t('storefront.checkout.edit')}</Text>
          </TouchableOpacity>
        }
      >
        {[
          { icon: 'person-outline', value: name },
          { icon: 'call-outline', value: phone },
          { icon: 'mail-outline', value: email },
          { icon: 'navigate-outline', value: location?.address },
          { icon: 'home-outline', value: address },
          { icon: 'document-text-outline', value: notes },
          { icon: 'bicycle-outline', value: selectedShipping ? `${selectedShipping.name}${expressSelected ? ` · ${selectedShipping.expressOption?.label || t('storefront.checkout.express')}` : ''}` : '' },
        ]
          .filter((r) => !!r.value)
          .map((r, i) => (
            <View key={i} style={s.inlineRow}>
              <Ionicons name={r.icon as any} size={16} color={colors.text.secondary as string} />
              <Text style={s.reviewValue}>{r.value}</Text>
            </View>
          ))}
      </Card>

      {summaryCard(t('storefront.checkout.summary'))}
    </>
  );

  const isLastStep = step === STEPS.length - 1;

  return (
    <View style={s.screen}>
      <StorefrontHeader title={t('storefront.checkout.title')} onBack={handleBack} backIcon={step === 0 ? 'close' : 'back'} />

      {/* Step indicator */}
      <View style={s.stepsWrap}>
        <View style={s.steps}>
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              {i > 0 ? <View style={[s.stepLine, i <= step && s.stepLineOn]} /> : null}
              <TouchableOpacity style={s.step} disabled={i >= step} onPress={() => goStep(i)}>
                <View style={[s.stepCircle, i <= step && s.stepCircleOn]}>
                  {i < step ? (
                    <Ionicons name="checkmark" size={13} color={colors.text.onPrimary as string} />
                  ) : (
                    <Text style={[s.stepNum, i === step && s.stepNumOn]}>{i + 1}</Text>
                  )}
                </View>
                <Text style={[s.stepLabel, i === step && s.stepLabelOn]}>{label}</Text>
              </TouchableOpacity>
            </React.Fragment>
          ))}
        </View>
      </View>

      <BodyWrapper style={s.body} {...(Platform.OS === 'ios' ? { behavior: 'padding' as const } : {})}>
        <ScrollView ref={scrollRef} style={{ flex: 1 }} contentContainerStyle={s.scrollContent} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
          <View style={s.column}>
            <OrderMinimumsNotice report={minimums} style={{ marginBottom: 12 }} />
            {step === 0 && renderDetails()}
            {step === 1 && renderDelivery()}
            {step === 2 && renderReview()}
          </View>
        </ScrollView>

        <View style={[s.footer, { paddingBottom: 12 + insets.bottom }]}>
          <View style={s.column}>
            {submitError ? (
              <View style={s.submitError}>
                <Ionicons name="alert-circle" size={18} color={colors.error as string} />
                <Text style={s.submitErrorText}>{submitError}</Text>
              </View>
            ) : null}
            <View style={s.footerRow}>
              <View style={{ flex: 1 }}>
                <Text style={s.hint}>{totalItems === 1 ? t('storefront.oneItem') : t('storefront.itemsCount', { count: totalItems })}</Text>
                <Text style={s.footerTotal}>{formatMoney(currency, total)}</Text>
              </View>
              {isLastStep ? (
                <TouchableOpacity style={[s.primaryBtn, submitting && { opacity: 0.7 }]} disabled={submitting} onPress={handleSubmit} activeOpacity={0.85}>
                  {submitting ? (
                    <>
                      <ActivityIndicator size="small" color={colors.text.onPrimary as string} />
                      <Text style={s.primaryBtnText}>{t('storefront.checkout.placing')}</Text>
                    </>
                  ) : (
                    <>
                      <Ionicons name="checkmark-circle-outline" size={20} color={colors.text.onPrimary as string} />
                      <Text style={s.primaryBtnText}>{t('storefront.checkout.placeOrder', { total: formatMoney(currency, total) })}</Text>
                    </>
                  )}
                </TouchableOpacity>
              ) : (
                <TouchableOpacity style={s.primaryBtn} onPress={handleNext} activeOpacity={0.85}>
                  <Text style={s.primaryBtnText}>{t('storefront.checkout.continue')}</Text>
                  <DirectionalIcon name="arrow-forward" size={18} color={colors.text.onPrimary as string} />
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </BodyWrapper>

      <MapLocationPicker
        visible={showLocationPicker}
        onLocationSelect={(sel) => {
          setLocation(sel);
          setShowLocationPicker(false);
        }}
        onClose={() => setShowLocationPicker(false)}
      />
    </View>
  );
};

const getStyles = () =>
  StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: colors.background.secondary,
      ...Platform.select({
        web: { width: '100%', maxWidth: '100%', overflowX: 'hidden' as any, height: '100dvh' as any, maxHeight: '100dvh' as any, minHeight: 0, display: 'flex' as any, flexDirection: 'column' as any },
      }),
    } as any,
    column: { width: '100%', maxWidth: 680, alignSelf: 'center' },
    body: { flex: 1, minHeight: 0 },
    center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 32 },
    emptyText: { fontSize: 16, fontFamily: typography.fontFamily.semibold, color: colors.text.primary, textAlign: 'center' },

    stepsWrap: { backgroundColor: colors.background.surface, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border },
    steps: { flexDirection: 'row', alignItems: 'center', alignSelf: 'center', width: '100%', maxWidth: 520, paddingHorizontal: 20, paddingVertical: 12 },
    step: { alignItems: 'center', gap: 4 },
    stepLine: { flex: 1, height: 2, marginBottom: 18, marginHorizontal: 6, backgroundColor: colors.border },
    stepLineOn: { backgroundColor: colors.primary },
    stepCircle: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background.secondary, borderWidth: 1, borderColor: colors.border },
    stepCircleOn: { backgroundColor: colors.primary, borderColor: colors.primary },
    stepNum: { fontSize: 12, fontFamily: typography.fontFamily.bold, color: colors.text.secondary, textAlign: 'center' },
    stepNumOn: { color: colors.text.onPrimary },
    stepLabel: { fontSize: 11, fontFamily: typography.fontFamily.medium, color: colors.text.secondary, textAlign: 'center' },
    stepLabelOn: { color: colors.text.primary, fontFamily: typography.fontFamily.bold },

    scrollContent: { padding: 14, paddingBottom: 24 },
    card: {
      backgroundColor: colors.background.surface,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: colors.border,
      padding: 16,
      marginBottom: 12,
    },
    cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 14 },
    cardTitle: { flex: 1, fontSize: 15, fontFamily: typography.fontFamily.bold, color: colors.text.primary },

    field: { marginBottom: 12 },
    label: { fontSize: 12, fontFamily: typography.fontFamily.semibold, color: colors.text.secondary, marginBottom: 6 },
    input: {
      minHeight: 46,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.background.secondary,
      paddingHorizontal: 14,
      paddingVertical: 11,
      fontSize: 15,
      fontFamily: typography.fontFamily.primary,
      color: colors.text.primary,
      ...Platform.select({ web: { outlineStyle: 'none' } as any }),
    } as any,
    textArea: { minHeight: 76, textAlignVertical: 'top' } as any,
    inputError: { borderColor: colors.error },
    errorText: { fontSize: 12, fontFamily: typography.fontFamily.medium, color: colors.error, marginTop: 5 },
    hint: { fontSize: 12, lineHeight: 17, fontFamily: typography.fontFamily.primary, color: colors.text.secondary },
    linkText: { fontSize: 13, fontFamily: typography.fontFamily.semibold, color: colors.primary },
    inlineRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 5 },

    locationBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      padding: 12,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.background.secondary,
    },
    locationIcon: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background.surface },
    locationIconActive: { backgroundColor: colors.primary },
    locationAddr: { fontSize: 14, lineHeight: 19, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
    locationPlaceholder: { fontSize: 14, fontFamily: typography.fontFamily.semibold, color: colors.primary },

    option: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      padding: 12,
      borderRadius: 12,
      borderWidth: 1.5,
      borderColor: colors.border,
      marginBottom: 8,
    },
    optionSelected: { borderColor: colors.primary, backgroundColor: colors.primaryTransparent },
    optionName: { fontSize: 14, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
    optionDays: { fontSize: 12, fontFamily: typography.fontFamily.medium, color: colors.primary, marginTop: 2 },
    optionPrice: { fontSize: 14, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
    radio: { width: 20, height: 20, borderRadius: 10, borderWidth: 2, borderColor: colors.border, alignItems: 'center', justifyContent: 'center' },
    radioOn: { borderColor: colors.primary },
    radioDot: { width: 10, height: 10, borderRadius: 5, backgroundColor: colors.primary },
    checkbox: { width: 22, height: 22, borderRadius: 6, borderWidth: 2, borderColor: colors.border, alignItems: 'center', justifyContent: 'center' },
    checkboxOn: { backgroundColor: colors.primary, borderColor: colors.primary },

    codeRow: { flexDirection: 'row', gap: 8 },
    codeBtn: { minWidth: 84, borderRadius: 12, paddingHorizontal: 16, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primary },
    codeBtnText: { fontSize: 14, fontFamily: typography.fontFamily.bold, color: colors.text.onPrimary },
    appliedCode: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      padding: 12,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.success,
      backgroundColor: colors.background.secondary,
    },
    appliedCodeText: { fontSize: 14, fontFamily: typography.fontFamily.bold, color: colors.success },
    appliedCodeSave: { fontSize: 12, fontFamily: typography.fontFamily.primary, color: colors.text.secondary, marginTop: 2 },

    priceRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingVertical: 4 },
    priceLabel: { flex: 1, fontSize: 14, fontFamily: typography.fontFamily.primary, color: colors.text.secondary },
    priceVal: { fontSize: 14, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
    positive: { color: colors.success },
    divider: { height: StyleSheet.hairlineWidth, backgroundColor: colors.border, marginVertical: 10 },
    totalLabel: { fontSize: 16, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
    totalVal: { fontSize: 19, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
    payNote: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 12, padding: 10, borderRadius: 10, backgroundColor: colors.background.secondary },
    payNoteText: { flex: 1, fontSize: 13, fontFamily: typography.fontFamily.medium, color: colors.text.primary },

    reviewItem: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, paddingVertical: 10 },
    reviewItemBorder: { borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border },
    reviewImg: { width: 56, height: 56, borderRadius: 10, overflow: 'hidden', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background.secondary },
    reviewName: { fontSize: 14, lineHeight: 19, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
    reviewPrice: { fontSize: 14, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
    reviewValue: { flex: 1, fontSize: 14, lineHeight: 19, fontFamily: typography.fontFamily.primary, color: colors.text.primary },

    footer: {
      backgroundColor: colors.background.surface,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border,
      paddingHorizontal: 16,
      paddingTop: 12,
    },
    footerRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
    footerTotal: { fontSize: 19, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
    submitError: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      padding: 10,
      marginBottom: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: colors.error,
      backgroundColor: colors.background.secondary,
    },
    submitErrorText: { flex: 1, fontSize: 13, fontFamily: typography.fontFamily.medium, color: colors.error },
    primaryBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      height: 52,
      paddingHorizontal: 20,
      borderRadius: 14,
      backgroundColor: colors.primary,
      minWidth: 150,
      flexShrink: 1,
    },
    primaryBtnSmall: { borderRadius: 12, paddingHorizontal: 20, paddingVertical: 12, backgroundColor: colors.primary },
    primaryBtnText: { fontSize: 15, fontFamily: typography.fontFamily.bold, color: colors.text.onPrimary },
  });
